/**
 * SYNCED FROM SERVER — do not edit by hand.
 *
 * Source: `server/src/api/me/me.contract.ts` (or the shared bases under
 * `server/src/api/common/contracts/`).
 *
 * Regenerate: `cd api-client && bun run sync:contracts`.
 *
 * The server is the source of truth for contract definitions. This file
 * exists so the web SPA can import zod schemas from
 * `@event-manager/api-client/contracts/me.contract` without reaching into the
 * server workspace.
 */
/**
 * Me Contract — schemas for the current-user "me" surface.
 *
 * `GET /api/me/org-context` returns the caller's organization context
 * resolved FRESH from live DB state on every call (current `Member` rows for
 * regular users, the open `ImpersonationLog` row for impersonating
 * superusers). It deliberately replaces the old session-enrichment claims
 * (`user.orgId`, `session.activeOrganizationId`, `orgName`, `orgPlan`) that
 * used to ride on the auth cookie — those could go stale; this cannot.
 */
import { z } from "zod";
import { PermissionsMapSchema } from "./roles.contract";

export const MeOrgContextResponseSchema = z.object({
	/** Current org id, or null when the caller belongs to no org. */
	orgId: z.string().nullable(),
	/** Display name of the current org, or null when none. */
	orgName: z.string().nullable(),
	/** Billing plan of the current org (defaulted when none). */
	orgPlan: z.string(),
	/**
	 * The caller's role within the current org (owner/admin/secretary/guest),
	 * or null for a superuser impersonating an org they aren't a member of, or
	 * a user with no org.
	 */
	orgRole: z.string().nullable(),
	/**
	 * ISO timestamp the org was created (for "member since"); null when none.
	 * Optional so responses from a pre-rollout server still validate.
	 */
	orgCreatedAt: z.string().datetime().nullable().optional(),
	/**
	 * Live effective permissions for the current org. Missing keys are denied
	 * by consumers; owner/admin members receive every known feature key.
	 */
	permissions: PermissionsMapSchema,
});

export type MeOrgContextResponse = z.infer<typeof MeOrgContextResponseSchema>;
